import React, { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useFormContext } from 'react-hook-form';
import MaxCountMonth from '../MaxCountMonth/MaxCountMonth';
import { TypedService } from '../../../models/ServicePresets';
import ShareInfo from '../ShareInfo/ShareInfo';
import { CustomSkeletonElement } from '../../commons/CustomSkeletonElement';
import FormInputMonthRefactor from '../../inputs/FormInputMonthRefactor/FormInputMonthRefactor';
import ShareAlert from '../ShareAlert/ShareAlert';
import { PreparedCalculateData } from '../../../models/Calculate';
import { IPermission, IShare } from '../../../models/UserInfo';
import { DateVOv2 } from '../../../core/DateVOv2';

interface MonthRangeProps {
  serviceForPayment: TypedService | undefined;
  maxCountMoth: number;
  handleSliderChange: (number: number | undefined) => void;
  calculation: PreparedCalculateData | null;
  isPaidServiceSuspension: boolean;
  isLoadingCalculation: boolean;
  сonnectedShare50Percent: IShare | null | undefined;
  isCtv: boolean;
  Permissions: IPermission | undefined;
  isBonusPrepaidsAvailable: boolean;
}

const MonthRange: FC<MonthRangeProps> = ({
  serviceForPayment,
  maxCountMoth,
  handleSliderChange,
  calculation,
  isPaidServiceSuspension,
  isLoadingCalculation,
  сonnectedShare50Percent,
  isCtv,
  Permissions,
  isBonusPrepaidsAvailable,
}) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext();
  const billDiscountPercent =
    calculation && calculation.discount_amount > 0 && isPaidServiceSuspension && !calculation?.view.dsc_percent
      ? calculation?.view.ex_amo[0].dsc_percent
      : calculation?.view.dsc_percent || 0;

  const isMonthDiscount50Available = calculation?.view.dsc_percent === 50;
  const isAvailableMonthRangeExceedPromo =
    Number(maxCountMoth) > 0 &&
    (!isMonthDiscount50Available || (isMonthDiscount50Available && Number(maxCountMoth) <= 11)) &&
    !isPaidServiceSuspension &&
    !!calculation;

  const expiresShareAt =
    сonnectedShare50Percent && DateTime.fromSQL(сonnectedShare50Percent.ActiveTo).setZone('Europe/Kiev');
  const dateFrom = new DateVOv2({
    dateString: getValues().dateFrom,
    format: 'dd-MM-yyyy',
  }).getLuxonKiev();

  const isAvailableForPaymentShare50Percent = expiresShareAt && expiresShareAt > dateFrom;

  return (
    <div className="payment__content__options__number-of-month-selector">
      {!serviceForPayment ? (
        <CustomSkeletonElement
          skeletonContainerClassName="month-count-skeleton"
          skeletonChildrenClassName="month-count-skeleton__btn skeleton"
          childrenCount={1}
        />
      ) : (
        <MaxCountMonth isShow={!serviceForPayment?.Share} countMonth={maxCountMoth} />
      )}
      {maxCountMoth > 0 &&
      !isPaidServiceSuspension &&
      billDiscountPercent > 0 &&
      !calculation?.earn_bonus_percent &&
      isAvailableForPaymentShare50Percent ? (
        <ShareInfo amount={maxCountMoth} text={t('servicePayment.promo50percentMonth')} />
      ) : null}
      {!serviceForPayment?.Share ? (
        <FormInputMonthRefactor
          calculation={calculation}
          name="count"
          step={1}
          min={1}
          max={maxCountMoth}
          handleInputMonthChange={handleSliderChange}
          isShowDiscount={
            !!(
              isBonusPrepaidsAvailable &&
              !isCtv &&
              (!isPaidServiceSuspension ||
                (isPaidServiceSuspension && calculation?.view?.ex_amo?.length && !!calculation?.earn_bonus_percent))
            ) ||
            !!(
              !!Permissions?.Payments?.PrepayDiscount &&
              (billDiscountPercent > 0 || (calculation && calculation.discount_amount > 0)) &&
              (!isPaidServiceSuspension || (isPaidServiceSuspension && calculation?.view?.ex_amo?.length))
            )
          }
          isDiscount50Available={!isPaidServiceSuspension && calculation?.view.dsc_percent === 50}
          isBonuses={!(billDiscountPercent > 0 && !calculation?.earn_bonus_percent)}
          isLoading={isLoadingCalculation}
          isShownPresets={!isPaidServiceSuspension && !isAvailableForPaymentShare50Percent && !!calculation}
          isAvailableForPaymentShare50Percent={!!isAvailableForPaymentShare50Percent}
        />
      ) : null}
      {isAvailableMonthRangeExceedPromo && billDiscountPercent > 0 && !calculation?.earn_bonus_percent ? (
        <ShareAlert
          alertText={
            calculation?.view.dsc_percent === 50
              ? t('servicePayment.availableMonthsExtension')
              : t('servicePayment.exceededPromoPeriod')
          }
        />
      ) : null}
    </div>
  );
};

export default memo(MonthRange);
